<template>
  <main-wrapper>
    <the-header :slider="true">
      <div class="banner-slider">
        <swiper
          :style="{
            '--swiper-pagination-bullet-size': '18px',
            '--swiper-pagination-bullet-border-color': '#ffffff',
            '--swiper-pagination-bullet-inactive-color': '#000000',
            '--swiper-pagination-bullet-inactive-opacity': '1',
            '--swiper-pagination-color': '#ffffff',
          }"
          :direction="sliderDirection"
          :speed="600"
          :parallax="true"
          :modules="modules"
          :pagination="{
            clickable: true,
          }"
          :autoplay="{
            delay: 4500,
            disableOnInteraction: false,
          }"
          @slide-change="(swiper) => (this.activeSlide = swiper.activeIndex)"
        >
          <swiper-slide v-for="(slide, idx) in slides" :key="idx">
            <div
              class="parallax-bg"
              :style="{
                'background-image': `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),url(${slide.image})`,
              }"
              data-swiper-parallax="-23%"
            ></div>
            <div
              class="container-fluid py-6 title text-white display-5"
              data-swiper-parallax="-300"
            >
              <div class="container">
                <div class="row g-5 align-items-center">
                  <span
                    v-if="idx == 0 && activeSlide == idx"
                    data-aos="fade-right"
                    data-aos-delay="300"
                    data-aos-offset="100px"
                    data-aos-easing="ease-in"
                    >{{ slide.title }}</span
                  >
                  <span
                    v-else
                    class="parallax-text"
                    :class="{ 'fade-right': activeSlide == idx }"
                    >{{ slide.title }}</span
                  >
                </div>
              </div>
            </div>
          </swiper-slide>
        </swiper>
      </div>
    </the-header>

    <div class="container-fluid py-6">
      <div class="container">
        <div class="row g-5 align-items-center">
          <div class="col-lg-7 text-center text-lg-start">
            <h1
              class="text-dark mb-4"
              data-aos="zoom-in"
              data-aos-delay="200"
              data-aos-offset="200px"
              data-aos-easing="ease-in"
            >
              Embark on a journey into the future alongside us
            </h1>
            <p
              class="text-dark pb-3"
              data-aos="fade-up"
              data-aos-delay="600"
              data-aos-offset="200px"
              data-aos-easing="ease-in"
            >
              Welcome to our agile-driven hub, where we specialize in bespoke
              solutions for projects big or small. Our dedicated in-house
              leaders ensure maximum cost savings and scalable solutions,
              standing out with unwavering commitment to client satisfaction.
              Join us in embracing the future with excellence at every turn.
            </p>
            <anchor-button
              :_path="{ name: 'about' }"
              customClass="btn btn-primary rounded-pill border-2 py-3 px-5"
            >
              Learn More</anchor-button
            >
          </div>
          <div
            class="col-lg-5 text-center text-lg-start"
            data-aos="zoom-in"
            data-aos-delay="200"
            data-aos-offset="500px"
            data-aos-easing="ease-in"
          >
            <img
              class="img-fluid"
              loading="lazy"
              src="../assets/img/home-section.png"
              alt=""
            />
          </div>
        </div>
      </div>
    </div>

    <!-- About -->
    <div class="container-fluid pt-2 py-3">
      <div class="container">
        <div class="row g-5 align-items-center">
          <div
            class="col-lg-6 text-center text-lg-start"
            data-aos="zoom-in"
            data-aos-delay="200"
            data-aos-offset="200px"
            data-aos-once="true"
            data-aos-easing="ease-in"
          >
            <img
              class="img-fluid"
              loading="lazy"
              src="../assets//img//home-about.png"
            />
          </div>
          <div class="col-lg-6 text-center text-lg-start">
            <h2
              class="mb-4"
              data-aos="zoom-in"
              data-aos-delay="200"
              data-aos-offset="200px"
              data-aos-once="true"
              data-aos-easing="ease-in"
            >
              What Are We Made Of
            </h2>
            <p
              class="mb-4"
              data-aos="fade-up"
              data-aos-delay="600"
              data-aos-offset="200px"
              data-aos-once="true"
              data-aos-easing="ease-in"
            >
              We are a dynamic organization providing agile and customized
              solutions for projects of all sizes. Our commitment includes cost
              savings through scalable and efficient solutions. Each project is
              assigned an in-house leader and a dedicated team, ensuring
              tailored solutions and unwavering commitment to customer
              satisfaction.
            </p>

            <anchor-button
              :_path="{ name: 'about' }"
              customClass="btn btn-primary"
            >
              Learn More</anchor-button
            >
          </div>
        </div>
      </div>
    </div>

    <div class="container-fluid py-5 pb-0">
      <div class="container">
        <div class="mx-auto text-center">
          <h2
            class="mb-5"
            v-if="services.title"
            data-aos="zoom-in"
            data-aos-delay="200"
            data-aos-offset="200px"
            data-aos-once="true"
            data-aos-easing="ease-in"
          >
            {{ services.title }}
          </h2>
        </div>
        <div class="row g-4">
          <div
            class="col-lg-4 col-md-6 mx-auto"
            v-for="(service, idx) in services.services"
            :key="idx"
            style="cursor: pointer"
            @click="navigateToRoute(service.path)"
            data-aos="fade-up"
            data-aos-delay="600"
            data-aos-offset="200px"
            data-aos-once="true"
            data-aos-easing="ease-in"
          >
            <div
              class="card-item h-100 d-flex flex-column justify-content-center text-center rounded"
            >
              <div class="card-icon flex-shrink-0">
                <i :class="['fa-2x', service.icon]"></i>
              </div>
              <h5 class="mb-3">{{ service.title }}</h5>
              <p>
                {{ service.homeDescription }}
              </p>
            </div>
          </div>
        </div>
        <div
          class="mx-auto text-center py-5"
          data-aos="zoom-in"
          data-aos-delay="200"
          data-aos-offset="200px"
          data-aos-once="true"
          data-aos-easing="ease-in"
        >
          <anchor-button
            :_path="{ name: 'services' }"
            customClass="btn btn-primary"
          >
            Learn More</anchor-button
          >
        </div>
      </div>
    </div>

    <!-- Footer -->
    <the-footer />
  </main-wrapper>
</template>
<script>
import TheHeader from "../components/TheHeader.vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/pagination";
import { Parallax, Autoplay, Pagination } from "swiper/modules";

export default {
  components: {
    TheHeader,
    Swiper,
    SwiperSlide,
  },
  computed: {
    sliderDirection() {
      const device =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      if (device === false) return "vertical";
      else return "horizontal";
    },
  },
  methods: {
    navigateToRoute(route_) {
      this.$router.push(route_);
    },
  },
  data() {
    return {
      services: this.$store.state.services,
      activeSlide: 0,
      modules: [Parallax, Autoplay, Pagination],
      slides: [
        {
          image: require("../assets/img/slider/slider-1.jpg"),
          title: "Consulting and Customized Solutions",
        },
        {
          image: require("../assets/img/slider/slider-2.jpg"),
          title: "AI and IT Solutions",
        },
        {
          image: require("../assets/img/slider/slider-3.jpg"),
          title: "Data Analytics",
        },
        {
          image: require("../assets/img/slider/slider-4.jpg"),
          title: "Engineering Consultation",
        },
      ],
    };
  },
};
</script>
<style>
.swiper-slide,
.banner-slider {
  width: 100%;
  height: 90vh;
  overflow: hidden;
}
.swiper {
  width: 100%;
  height: 90vh;
  background: #000;
}

.swiper-slide {
  font-size: 18px;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  padding: 0;
}

.parallax-bg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 90vh;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}

.parallax-text {
  opacity: 0;
  transition: opacity 0.8s ease-in-out, transform 0.8s ease-in-out;
  transform: translateX(-100%);
}

.fade-right {
  opacity: 1;
  transform: translateX(0);
}

.swiper-slide .title {
  height: 100%;
  display: flex;
  align-items: center;
  font-weight: bolder;
}

.swiper-pagination-bullet {
  border: 2px solid white !important;
}
</style>
